import React, { useState } from 'react';
import axios from 'axios';
import { Activity, Eye, Upload, CheckCircle, AlertCircle, HeartPulse, ShieldCheck } from 'lucide-react';

const API_BASE = "/api/v1"; 

function App() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [irisData, setIrisData] = useState(null);
  const [ecgData, setEcgData] = useState(null);

  const handleIrisSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('left_eye', e.target.left.files[0]);
    formData.append('right_eye', e.target.right.files[0]);
    try {
      const res = await axios.post(`${API_BASE}/iris-screening`, formData);
      setIrisData(res.data);
      if (res.data.is_healthy) setStep(3);
      else setStep(2);
    } catch (err) {
      alert("Lỗi kết nối Backend!");
    } finally { setLoading(false); }
  };

  const handleEcgSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('hea', e.target.hea.files[0]);
    formData.append('dat', e.target.dat.files[0]);
    formData.append('age', e.target.age.value);
    formData.append('sex', e.target.sex.value);
    try {
      const res = await axios.post(`${API_BASE}/ecg-analysis`, formData);
      setEcgData(res.data);
      setStep(3);
    } catch (err) {
      alert("Lỗi phân tích ECG!");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900">
      <nav className="bg-blue-800 text-white shadow-lg p-5">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-lg text-blue-800"><HeartPulse size={28} /></div>
            <div>
              <h1 className="text-xl font-black uppercase tracking-tighter">Neural Titan</h1>
              <p className="text-[10px] opacity-80 font-bold uppercase tracking-widest">Health Screening System</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow container mx-auto py-12 px-4">
        <div className="flex justify-center items-center mb-12 gap-2 md:gap-8">
          <StepIcon icon={<Eye />} active={step === 1} done={step > 1} label="Sàng lọc Iris" />
          <div className={`h-1 w-12 rounded ${step > 1 ? 'bg-blue-600' : 'bg-slate-200'}`} />
          <StepIcon icon={<Activity />} active={step === 2} done={step > 2} label="Phân tích ECG" />
          <div className={`h-1 w-12 rounded ${step > 2 ? 'bg-blue-600' : 'bg-slate-200'}`} />
          <StepIcon icon={<ShieldCheck />} active={step === 3} done={step === 3} label="Kết quả" />
        </div>

        {step === 1 && (
          <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-2xl p-10 border border-slate-100 animate-in fade-in duration-500">
            <h2 className="text-2xl font-bold text-center mb-8">Bước 1: Kiểm tra Mống mắt</h2>
            <form onSubmit={handleIrisSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputFile label="Mắt Trái" name="left" />
                <InputFile label="Mắt Phải" name="right" />
              </div>
              <button disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl shadow-lg transition-all active:scale-95 flex justify-center items-center gap-2">
                {loading ? "Đang xử lý AI..." : <><Upload size={18}/> Chẩn đoán ngay</>}
              </button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl p-10 border-t-8 border-amber-400 animate-in slide-in-from-bottom-10 duration-500">
            <div className="flex items-start gap-4 mb-8 bg-amber-50 p-5 rounded-2xl border border-amber-100">
              <AlertCircle className="text-amber-600 shrink-0 mt-1" size={24} />
              <p className="text-sm text-amber-800">Phát hiện dấu hiệu bất thường từ mống mắt. Vui lòng tải file ECG (.hea, .dat) để phân tích chuyên sâu.</p>
            </div>
            <form onSubmit={handleEcgSubmit} className="grid grid-cols-2 gap-6">
              <div className="col-span-1 space-y-2"><label className="text-xs font-bold uppercase">File .hea</label><input type="file" name="hea" required className="w-full border p-2 rounded-xl" /></div>
              <div className="col-span-1 space-y-2"><label className="text-xs font-bold uppercase">File .dat</label><input type="file" name="dat" required className="w-full border p-2 rounded-xl" /></div>
              <div className="col-span-1 space-y-2"><label className="text-xs font-bold uppercase">Tuổi</label><input type="number" name="age" required className="w-full border p-2 rounded-xl" /></div>
              <div className="col-span-1 space-y-2"><label className="text-xs font-bold uppercase">Giới tính</label><select name="sex" className="w-full border p-2 rounded-xl bg-white"><option value="1">Nam</option><option value="0">Nữ</option></select></div>
              <button disabled={loading} className="col-span-2 bg-slate-800 hover:bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-xl transition-all">
                {loading ? "Đang phân tích ECG..." : "Tiến hành phân tích chuyên sâu"}
              </button>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl p-8 border border-slate-100 animate-in zoom-in-95 duration-500">
            <div className="text-center mb-8">
              <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
              <h2 className="text-3xl font-black uppercase text-slate-900">Báo cáo chẩn đoán AI</h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cột 1: Iris Results */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                  <h4 className="text-blue-700 font-bold uppercase text-[10px] tracking-widest mb-4">Kết quả Mống mắt</h4>
                  <p className="text-xs font-bold mb-2">Mắt Trái:</p>
                  <ProgressBar list={irisData?.predictions?.left} />
                  <p className="text-xs font-bold mt-4 mb-2">Mắt Phải:</p>
                  <ProgressBar list={irisData?.predictions?.right} />
                </div>
              </div>

              {/* Cột 2 & 3: ECG Chart & Details */}
              <div className="lg:col-span-2 space-y-6">
                {ecgData && (
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <h4 className="text-red-700 font-bold uppercase text-[10px] tracking-widest mb-4">Biểu đồ 12 đạo trình & Xác suất bệnh lý</h4>
                    <img src={ecgData.visual_chart} alt="ECG" className="w-full rounded-lg mb-6 shadow-sm border border-slate-200" />
                    <div className="grid md:grid-cols-2 gap-4">
                      {ecgData.predictions.map((item, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200">
                          <div className="flex justify-between text-xs font-bold mb-1">
                            <span>{item.label}</span>
                            <span className={item.confidence > 50 ? 'text-red-600' : 'text-slate-500'}>{item.confidence}%</span>
                          </div>
                          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-blue-600 h-full transition-all duration-1000" style={{ width: `${item.confidence}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-slate-900 text-white rounded-2xl">
              <h4 className="font-bold flex items-center gap-2 text-blue-300 mb-2"><ShieldCheck size={18}/> KẾT LUẬN</h4>
              <p className="text-sm opacity-90 leading-relaxed">{ecgData?.recommendation || irisData?.message}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Sub-components hỗ trợ
const StepIcon = ({ icon, active, done, label }) => (
  <div className={`flex flex-col items-center ${active || done ? 'text-blue-700' : 'text-slate-300'}`}>
    <div className={`p-4 rounded-2xl border-2 transition-all ${active ? 'border-blue-600 bg-white scale-110' : done ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white'}`}>{icon}</div>
    <span className="text-[10px] mt-3 font-black uppercase">{label}</span>
  </div>
);

const InputFile = ({ label, name }) => (
  <div className="space-y-2">
    <label className="text-xs font-bold text-slate-600 uppercase">{label}</label>
    <input type="file" name={name} required className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" />
  </div>
);

const ProgressBar = ({ list }) => (
  <div className="space-y-2">
    {list && Object.entries(list).map(([label, val]) => (
      <div key={label}>
        <div className="flex justify-between text-[10px] mb-1"><span>{label}</span><span>{val}%</span></div>
        <div className="w-full bg-white h-1 rounded-full overflow-hidden border border-slate-200">
          <div className="bg-blue-500 h-full" style={{ width: `${val}%` }} />
        </div>
      </div>
    ))}
  </div>
);

export default App;